import React, { useState } from 'react';
import lodashFind from 'lodash/fp/find';

import {
  INVENTORY_ITEM_STATUS_TO_LABEL_MAP,
  INVENTORY_ITEM_TYPE_TO_LABEL_MAP,
} from 'common/inventoryConstants';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrDataTableUI from 'client/components/SrDataTableUI';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrInventoryItemScanLogsModal from 'client/components/SrInventoryItemsDataTable/SrInventoryItemScanLogsModal';
import SrSimpleDropdownWithSearchBar from 'client/components/SrSimpleDropdownWithSearchbar';
import { SR_TABLE_THEME_HISTORICAL } from 'client/components/SrTable';
import {
  DATA_TABLE_SOURCE_SUNSETUP_INVENTORY_ITEMS,
  DATA_TABLE_VIEW_ID_SUNSETUP_INVENTORY_ITEMS,
} from 'common/dataTableConstants';

function getDataTableSettingsFromProps() {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_SUNSETUP_INVENTORY_ITEMS,
    rootViewId: DATA_TABLE_VIEW_ID_SUNSETUP_INVENTORY_ITEMS,
    initialViewSettings: {},
    itemsPropName: 'items',
    count: 20,
  };

  return dataTableSettings;
}

const SrInventoryItemsDataTable = ({ maxHeight, markets, drivers }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [selectedMarketId, setSelectedMarketId] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);

  const marketFilterLabel = selectedMarketId
    ? 'Market: ' + lodashFind({ id: selectedMarketId }, markets).displayName
    : 'Market';

  const driverFilterLabel = selectedDriverId
    ? 'Driver: ' + lodashFind({ id: selectedDriverId }, drivers).name
    : 'Driver';

  const itemTypeFilterLabel = selectedItemType
    ? 'Type: ' + INVENTORY_ITEM_TYPE_TO_LABEL_MAP[selectedItemType]
    : 'Type';

  const headers = [
    {
      content: 'Type',
      dataTableFieldId: 'inventoryItemTypeId',
      width: '15%',
    },
    {
      content: 'Serial Number',
      dataTableFieldId: 'serialNumber',
      width: '15%',
    },
    {
      content: 'Owner',
      dataTableFieldId: 'ownerName',
      width: '10%',
    },
    {
      content: 'Market',
      dataTableFieldId: 'marketDisplayName',
      width: '15%',
    },
    {
      content: 'Status',
      dataTableFieldId: 'status',
      width: '15%',
    },
    {
      content: 'Item Scans',
      dataTableFieldId: 'actions',
      width: '15%',
    },
  ];

  return (
    <SrDataTableConnected {...getDataTableSettingsFromProps()}>
      {({ items, viewSettings, dataTableApiState, onInfiniteScrollWrapperRef }) => {
        // eslint-disable-line max-len
        return (
          <>
            <SrBox width="100%" marginBottom="16px" display="flex" justifyContent="flex-end">
              <SrBox margin="0px 8px">
                <SrSimpleDropdownWithSearchBar
                  title={marketFilterLabel}
                  options={markets.map((market) => ({
                    isActive: selectedMarketId === market.id,
                    title: market.displayName,
                    id: market.id,
                  }))}
                  onChange={(value) => {
                    setSelectedMarketId(value);
                    viewSettings.update({
                      ...viewSettings,
                      filter: {
                        ...viewSettings.filter,
                        marketId: value,
                      },
                    });
                  }}
                />
              </SrBox>
              <SrBox margin="0px 8px">
                <SrSimpleDropdownWithSearchBar
                  title={driverFilterLabel}
                  options={drivers.map((driver) => ({
                    isActive: selectedDriverId === driver.id,
                    title: driver.name,
                    id: driver.id,
                  }))}
                  onChange={(value) => {
                    setSelectedDriverId(value);
                    viewSettings.update({
                      ...viewSettings,
                      filter: {
                        ...viewSettings.filter,
                        driverId: value,
                      },
                    });
                  }}
                />
              </SrBox>
              <SrBox margin="0px 8px">
                <SrSimpleDropdownWithSearchBar
                  title={itemTypeFilterLabel}
                  options={Object.keys(INVENTORY_ITEM_TYPE_TO_LABEL_MAP).map((itemType) => ({
                    isActive: selectedItemType === itemType,
                    title: INVENTORY_ITEM_TYPE_TO_LABEL_MAP[itemType],
                    id: itemType,
                  }))}
                  onChange={(value) => {
                    setSelectedItemType(value);
                    viewSettings.update({
                      ...viewSettings,
                      filter: {
                        ...viewSettings.filter,
                        inventoryItemTypeId: value,
                      },
                    });
                  }}
                />
              </SrBox>
            </SrBox>
            <SrBox maxHeight={maxHeight || 'none'} overflowY="scroll">
              <div ref={onInfiniteScrollWrapperRef}>
                <SrDataTableUI
                  sticky
                  theme={SR_TABLE_THEME_HISTORICAL}
                  dataTableViewSettings={viewSettings}
                  headers={headers}
                  minWidth="100%"
                  rows={items.map((item) => {
                    return {
                      cells: [
                        {
                          content: (
                            <SrText fontSize="12px">
                              {INVENTORY_ITEM_TYPE_TO_LABEL_MAP[item.inventoryItemTypeId]}
                            </SrText>
                          ),
                        },
                        {
                          content: <SrText fontSize="12px">{item.serialNumber}</SrText>,
                        },
                        {
                          content: <SrText fontSize="12px">{item.ownerName}</SrText>,
                        },
                        {
                          content: <SrText fontSize="12px">{item.marketDisplayName}</SrText>,
                        },
                        {
                          content: (
                            <SrText fontSize="12px">
                              {INVENTORY_ITEM_STATUS_TO_LABEL_MAP[item.status]}
                            </SrText>
                          ),
                        },
                        {
                          content: (
                            <SrText
                              cursor="pointer"
                              color="blue300"
                              onClick={() => setSelectedItem(item)}
                              fontSize="12px"
                            >
                              See Scans
                            </SrText>
                          ),
                        },
                      ],
                    };
                  })}
                />
                {selectedItem && (
                  <SrInventoryItemScanLogsModal
                    itemScansLogs={selectedItem.itemScans || []}
                    itemName={INVENTORY_ITEM_TYPE_TO_LABEL_MAP[selectedItem.inventoryItemTypeId]}
                    itemSerialNumber={selectedItem.serialNumber}
                    itemStatus={INVENTORY_ITEM_STATUS_TO_LABEL_MAP[selectedItem.status]}
                    onClose={() => setSelectedItem(null)}
                  />
                )}
                {dataTableApiState.inProgress && (
                  <div>
                    <div className="loading-gif clearfix" />
                  </div>
                )}
              </div>
            </SrBox>
          </>
        );
      }}
    </SrDataTableConnected>
  );
};

export default SrInventoryItemsDataTable;
